/*
 * Optical spectrum service (Spectrum as a Service) — single source of truth.
 *
 * Owner decision, 23 Oct 2026: dedicated spectrum is offered across the
 * whole DWDM network in two slot widths, 112.5 GHz and 150 GHz, priced on
 * request. The customer runs their own transponders in the slot.
 *
 * Slot-to-optics fit (112.5 GHz → 800G-class) is a general industry rule of thumb (symbol rate of the
 * transponder generation), not a promise of a specific capacity on a
 * specific route: reach and capacity depend on the customer's optics and
 * the route's optical budget, which engineering confirms per quote.
 *
 * Delivery: our own DWDM line system. Dubai is reached through a long-haul
 * partner, so spectrum on that leg is on request and not listed here.
 */
import { popsFor } from "@/data/service-availability";
import { PARTNER_POP_IDS } from "@/lib/network-metrics";

type Bi = { en: string; bg: string };

export interface SpectrumSlot {
  ghz: number;
  /** Transponder class that typically fits this slot width. */
  fits: Bi;
}

export const SPECTRUM_SLOTS: readonly SpectrumSlot[] = [
  { ghz: 112.5, fits: { en: "800G-class", bg: "клас 800G" } },
  // No rate is named for 150 GHz (owner, 23 Oct 2026): only that it leaves
  // room for wider, next-generation optics.
  { ghz: 150, fits: { en: "next-generation", bg: "от следващо поколение" } },
];

export function formatGhz(ghz: number, lang: "en" | "bg"): string {
  return `${ghz.toLocaleString(lang === "bg" ? "bg-BG" : "en-US")} GHz`;
}

/** "112.5 or 150 GHz" / "112,5 или 150 GHz". */
export const SPECTRUM_SLOTS_LABEL: Bi = {
  en: `${SPECTRUM_SLOTS.map((s) => s.ghz.toLocaleString("en-US")).join(" or ")} GHz`,
  bg: `${SPECTRUM_SLOTS.map((s) => s.ghz.toLocaleString("bg-BG")).join(" или ")} GHz`,
};

/** "112.5 / 150 GHz" for compact spec tiles. */
export const SPECTRUM_SLOTS_SHORT: Bi = {
  en: `${SPECTRUM_SLOTS.map((s) => s.ghz.toLocaleString("en-US")).join(" / ")} GHz`,
  bg: `${SPECTRUM_SLOTS.map((s) => s.ghz.toLocaleString("bg-BG")).join(" / ")} GHz`,
};

/**
 * Highest line rate offered today, and only through spectrum: the customer
 * runs 800G-class optics in a 112.5 GHz slot after we measure the line
 * parameters on the specific route (owner, 23 Oct 2026). Not a managed
 * wavelength product.
 */
export const SPECTRUM_HIGH_RATE = "800G";

/** PoPs on our own DWDM line system (partner-reached PoPs excluded). */
export const SPECTRUM_POP_IDS: readonly string[] = popsFor("dwdm").filter(
  (id) => !PARTNER_POP_IDS.includes(id),
);
